import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { CatalogPage } from '~/components/catalog/catalog-page'
import { closeProductSheet, openProductSheet } from '~/components/catalog/product-sheet'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/products/$slug')({
  component: CatalogProductRoute,
})

/**
 * The deep link to one product — what "Copiar link" produces and what the
 * assistant's `product_grid` cards point at.
 *
 * It renders the catalog behind the ficha rather than a bare dialog on an
 * empty page: closing the sheet should leave someone IN the catalog, which is
 * where they wanted to be, not on a blank route with a back button.
 *
 * HISTORY belongs to the route here, not to the sheet (`ownsHistory: false`).
 * This URL is already an entry, so Back leaves the route — and the cleanup
 * below is what makes the sheet leave with it, rather than staying up over a
 * page nobody asked it to cover.
 */
function CatalogProductRoute() {
  const { slug, workspaceSlug } = useParams({
    from: '/_authenticated/$workspaceSlug/products/$slug',
  })
  const navigate = useNavigate()

  useEffect(() => {
    openProductSheet({
      onClose: () =>
        void navigate({
          params: { workspaceSlug },
          replace: true,
          to: '/$workspaceSlug/products',
        }),
      // THE ROUTE OWNS WHICH PRODUCT IS SHOWN. Opening a Similar one
      // navigates, so the URL and the ficha move together and Back walks the
      // chain of products the way it looks like it should.
      //
      // Unmounted BEFORE the navigation, not after: the sheet closes itself
      // when the app navigates (`useDismissOnNavigate`), and that close runs
      // `onClose`, which sends the URL back to the catalog. Left to race,
      // the Similar click landed on `/products` instead of on the product.
      // Unmounting first means there is nothing left to react to the move,
      // and this route's own effect opens the next sheet.
      onOpenSimilar: (next) => {
        closeProductSheet()
        void navigate({
          params: { slug: next, workspaceSlug },
          to: '/$workspaceSlug/products/$slug',
        })
      },
      ownsHistory: false,
      slug,
    })
    return closeProductSheet
  }, [navigate, slug, workspaceSlug])

  return <CatalogPage />
}
